/**
 * Lightweight TeX parser for academic titles and abstracts.
 *
 * Text-level LaTeX (accents, Greek letters, relations, arrows, formatting)
 * is handled here; real math is delegated to KaTeX.
 */
import katex from 'katex';


export interface TexParseOptions {
  allowMath?: boolean;
  allowLinks?: boolean;
}

// Combining diacritic marks for single-symbol LaTeX accents (\'a, \"o, \^e, ...)
const SYMBOL_ACCENTS: Record<string, string> = {
  "'": '\u0301', // acute
  '`': '\u0300', // grave
  '"': '\u0308', // diaeresis / umlaut
  '^': '\u0302', // circumflex
  '~': '\u0303', // tilde
  '=': '\u0304', // macron
  '.': '\u0307', // dot above
};

// Combining diacritic marks for named LaTeX accents (\v{s}, \c{c}, \H{o}, ...)
const NAMED_ACCENTS: Record<string, string> = {
  u: '\u0306', // breve
  v: '\u030C', // caron
  c: '\u0327', // cedilla
  H: '\u030B', // double acute (Hungarian)
  k: '\u0328', // ogonek
  r: '\u030A', // ring above
  d: '\u0323', // dot below
  b: '\u0331', // bar below
};

// Whole-letter LaTeX shortcuts (\ss, \o, \ae, ...)
const LATEX_LETTERS: Record<string, string> = {
  ss: 'ß', o: 'ø', O: 'Ø', l: 'ł', L: 'Ł',
  ae: 'æ', AE: 'Æ', oe: 'œ', OE: 'Œ',
  aa: 'å', AA: 'Å', i: 'ı', j: 'ȷ',
};

/**
 * Decode the small set of HTML entities we routinely see embedded in
 * scraped metadata (arXiv/OAI feeds quote backslash accents as `&#34;` or
 * `&quot;`). Done in pure JS so it works without a DOM (e.g. SSR / tests)
 * and so accent parsing below can find `\"a`, `\'e`, …
 */
const decodeBasicEntities = (text: string): string =>
  text
    .replace(/&#x([0-9a-fA-F]+);/g, (m, hex) => {
      const cp = parseInt(hex, 16);
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m;
    })
    .replace(/&#(\d+);/g, (m, dec) => {
      const cp = parseInt(dec, 10);
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m;
    })
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/**
 * Convert LaTeX accent escapes (\'a, \"o, \v{s}, \H{o}, \ss, ...) to Unicode.
 * Robust to braced forms (\'{a}), dotless letters (\"{\i}), HTML-encoded
 * backslash quoting (`K\&#34;ahler` → `Kähler`), LaTeX dashes (`--` / `---`),
 * and stray trailing marks left behind by truncated/garbled metadata.
 */
export const applyLatexAccents = (text: string): string => {
  if (!text) return text;
  let result = decodeBasicEntities(text);

  // Named accents with braces: \v{s} \H{o} \c{c} ...
  result = result.replace(
    /\\([uvcHkrdb])\{\\?([a-zA-Z])\}/g,
    (_, mark, ch) => (ch + NAMED_ACCENTS[mark]).normalize('NFC')
  );

  // Symbol accents with braces, optionally wrapping the dotless form: \'{a}, \"{\i}
  result = result.replace(
    /\\(['`"^~=.])\{\\?([a-zA-Z])\}/g,
    (_, mark, ch) => (ch + SYMBOL_ACCENTS[mark]).normalize('NFC')
  );

  // Symbol accents without braces: \'a \"o \^e \~n \=a \.a \`e
  result = result.replace(
    /\\(['`"^~=.])([a-zA-Z])/g,
    (_, mark, ch) => (ch + SYMBOL_ACCENTS[mark]).normalize('NFC')
  );

  // Whole-letter shortcuts. Two-letter forms first so they win.
  result = result.replace(
    /\\(ss|AE|ae|OE|oe|AA|aa)(?![a-zA-Z])/g,
    (_, key) => LATEX_LETTERS[key]
  );
  result = result.replace(
    /\\([oOlLij])(?![a-zA-Z])/g,
    (_, key) => LATEX_LETTERS[key]
  );

  // Trailing/standalone accent marks with no letter (e.g. "Bodn\'" truncations).
  result = result.replace(/\\(['`"^~=.])(?![a-zA-Z{])/g, (_, mark) => mark);

  // LaTeX dashes: --- → em-dash, -- → en-dash. Longest match first.
  result = result.replace(/---/g, '\u2014').replace(/--/g, '\u2013');

  return result;
};


/**
 * Parse math font commands (mathrm, mathbf, etc.)
 */
const parseMathFontCommands = (text: string): string => {
  let result = text;
  
  // Math font commands - process these first to handle nested content
  result = result.replace(/\\mathrm\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-normal not-italic">$1</span>');
  result = result.replace(/\\mathbf\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<strong>$1</strong>');
  result = result.replace(/\\mathit\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<em>$1</em>');
  result = result.replace(/\\mathcal\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-serif italic">$1</span>');
  result = result.replace(/\\mathbb\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-serif font-bold">$1</span>');
  result = result.replace(/\\mathsf\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-sans">$1</span>');
  result = result.replace(/\\mathtt\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-mono">$1</span>');
  result = result.replace(/\\text\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<span class="font-normal not-italic">$1</span>');
  
  return result;
};

/** Macros commonly used in arXiv metadata that KaTeX does not know. */
const KATEX_MACROS: Record<string, string> = {
  '\\ii': '\\mathrm{i}',
  '\\ee': '\\mathrm{e}',
  '\\dd': '\\mathrm{d}',
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Render a math fragment with KaTeX. Never throws: malformed TeX is rendered
 * in KaTeX's error style, and a hard failure falls back to the escaped source.
 */
const renderMath = (source: string, displayMode: boolean): string => {
  // Scraped metadata often arrives with `<`, `>` and `&` HTML-encoded inside the
  // math source; KaTeX must see the real characters.
  const src = decodeBasicEntities(source).trim();
  if (!src) return '';

  try {
    const html = katex.renderToString(src, {
      throwOnError: false,
      displayMode,
      output: 'htmlAndMathml',
      strict: 'ignore',
      trust: false,
      macros: { ...KATEX_MACROS },
    });
    // Display math is wrapped inline-block so a card title or a one-line
    // abstract preview never gets a forced block break.
    return displayMode
      ? `<span class="tex-display">${html}</span>`
      : html;
  } catch {
    return escapeHtml(src);
  }
};

type TexSegment =
  | { type: 'text'; value: string }
  | { type: 'math'; value: string; display: boolean };

const MATH_OPENERS: { open: string; close: string; display: boolean }[] = [
  { open: '$$', close: '$$', display: true },
  { open: '\\[', close: '\\]', display: true },
  { open: '\\(', close: '\\)', display: false },
  { open: '$', close: '$', display: false },
];

/**
 * Split raw TeX into text and math segments, understanding `$…$`, `$$…$$`,
 * `\(…\)` and `\[…\]`. Escaped `\$` stays a literal dollar. Unterminated
 * delimiters degrade to text rather than swallowing the rest of the string.
 */
const splitMathSegments = (text: string): TexSegment[] => {
  const segments: TexSegment[] = [];
  let buffer = '';
  let i = 0;

  const flushText = () => {
    if (buffer) segments.push({ type: 'text', value: buffer });
    buffer = '';
  };

  while (i < text.length) {
    // Escaped dollar: literal.
    if (text.startsWith('\\$', i)) {
      buffer += '$';
      i += 2;
      continue;
    }

    const opener = MATH_OPENERS.find((candidate) => text.startsWith(candidate.open, i));
    if (!opener) {
      buffer += text[i];
      i += 1;
      continue;
    }

    const contentStart = i + opener.open.length;
    const closeIndex = text.indexOf(opener.close, contentStart);
    if (closeIndex === -1) {
      // Unterminated: treat the delimiter as plain text.
      buffer += text.slice(i, contentStart);
      i = contentStart;
      continue;
    }

    const content = text.slice(contentStart, closeIndex);
    flushText();
    segments.push({ type: 'math', value: content, display: opener.display });
    i = closeIndex + opener.close.length;
  }

  flushText();
  return segments;
};

// Unknown macro, optionally with a braced or single-token argument.
const UNKNOWN_MACRO = /\\[a-zA-Z]+(?:\s*\{[^{}]*\}|[ \t]+[A-Za-z0-9])?/g;
// A sub/superscript chain written outside of math mode, e.g. `O(n^{-1})`,
// `F_G(t)` or `\tau_\mathrm{c}`. The base symbol is optional so `)_{vu}` works.
const SCRIPT_ARG = String.raw`(?:\{[^{}]*\}|\\[a-zA-Z]+(?:\{[^{}]*\})?|[+-]?[\p{L}\p{N}]+)`;
const SCRIPT_CHAIN = new RegExp(
  String.raw`(\\[a-zA-Z]+|[\p{L}\p{N}])?((?:[_^]${SCRIPT_ARG})+)`,
  'gu',
);

/** Placeholder stash: keeps rendered KaTeX out of later regex passes. */
const createStash = () => {
  const items: string[] = [];
  return {
    put: (rendered: string) => {
      items.push(rendered);
      return `\u0000${items.length - 1}\u0000`;
    },
    restore: (html: string) =>
      html.replace(/\u0000(\d+)\u0000/g, (_m, index) => items[Number(index)] ?? ''),
  };
};

type Stash = ReturnType<typeof createStash>;

/** Run a replacement only on text nodes, never inside generated HTML tags. */
const mapTextParts = (html: string, fn: (part: string) => string): string =>
  html
    .split(/(<[^>]*>)/)
    .map((part) => (!part || part.startsWith('<') ? part : fn(part)))
    .join('');

/**
 * Anything that still looks like LaTeX after the text pipeline (unknown macros)
 * is routed through KaTeX instead of being printed with raw backslashes.
 */
const promoteUnknownMacros = (html: string, stash: Stash): string =>
  mapTextParts(html, (part) =>
    part.replace(UNKNOWN_MACRO, (match) => stash.put(renderMath(match, false))),
  );

/**
 * Parse a non-math text segment: LaTeX accents, text formatting commands and
 * the symbol shortcuts (Greek letters, relations, arrows) that arXiv abstracts
 * routinely use outside of `$…$`.
 */
const parseTextSegment = (text: string, options: TexParseOptions = {}): string => {
  if (!text) return '';

  const { allowLinks = true } = options;
  const stash = createStash();

  // Normalise LaTeX accent escapes to Unicode BEFORE other backslash handling
  // so things like `Tur\'an` become `Turán` cleanly.
  let parsed = applyLatexAccents(text);

  // Sub/superscript clusters are typeset by KaTeX before the symbol tables run,
  // so `\tau_\mathrm{c}` stays one piece of math instead of being torn apart.
  parsed = parsed.replace(SCRIPT_CHAIN, (match, base: string, scripts: string) => {
    const rendered = renderMath(`${base || '{}'}${scripts}`, false);
    return rendered ? stash.put(rendered) : match;
  });

  // Text formatting commands (using better regex for nested content)
  parsed = parsed.replace(/\\textbf\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<strong>$1</strong>');
  parsed = parsed.replace(/\\textit\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<em>$1</em>');
  parsed = parsed.replace(/\\emph\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<em>$1</em>');
  parsed = parsed.replace(/\\underline\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g, '<u>$1</u>');

  
  // Math font commands (also support outside of math mode)
  parsed = parseMathFontCommands(parsed);
  

  
  // Mathematical functions and operators
  parsed = parsed.replace(/\\log\b/g, '<span class="math-function">log</span>');
  parsed = parsed.replace(/\\ln\b/g, '<span class="math-function">ln</span>');
  parsed = parsed.replace(/\\exp\b/g, '<span class="math-function">exp</span>');
  parsed = parsed.replace(/\\min\b/g, '<span class="math-function">min</span>');
  parsed = parsed.replace(/\\max\b/g, '<span class="math-function">max</span>');
  
  // Arrow symbols (for use outside math mode)
  parsed = parsed.replace(/\\rightarrow/g, '→');
  parsed = parsed.replace(/\\leftarrow/g, '←');
  parsed = parsed.replace(/\\leftrightarrow/g, '↔');
  parsed = parsed.replace(/\\Rightarrow/g, '⇒');
  parsed = parsed.replace(/\\Leftarrow/g, '⇐');
  parsed = parsed.replace(/\\Leftrightarrow/g, '⇔');
  parsed = parsed.replace(/\\to/g, '→');
  
  // Spacing commands (for use outside math mode)
  parsed = parsed.replace(/\\,/g, ' '); // thin space
  parsed = parsed.replace(/\\;/g, ' '); // medium space
  parsed = parsed.replace(/\\:/g, ' '); // thick space
  parsed = parsed.replace(/\\!/g, '');  // negative thin space (remove)
  parsed = parsed.replace(/\\ /g, ' '); // explicit space
  parsed = parsed.replace(/\\quad/g, '  '); // quad space
  parsed = parsed.replace(/\\qquad/g, '    '); // double quad space
  
  // Note: Accents (\bar, \hat, \tilde, etc.) are handled in parseNestedTex for math mode
  // They are not duplicated here to avoid double processing
  
  // Mathematical symbols
  parsed = parsed.replace(/\\times/g, '×');
  parsed = parsed.replace(/\\cdot/g, '·');
  parsed = parsed.replace(/\\leq/g, '≤');
  parsed = parsed.replace(/\\geq/g, '≥');
  parsed = parsed.replace(/\\neq/g, '≠');
  parsed = parsed.replace(/\\approx/g, '≈');
  parsed = parsed.replace(/\\infty/g, '∞');
  parsed = parsed.replace(/\\in\b/g, '∈');
  parsed = parsed.replace(/\\notin/g, '∉');
  parsed = parsed.replace(/\\subset/g, '⊂');
  parsed = parsed.replace(/\\supset/g, '⊃');
  parsed = parsed.replace(/\\cap/g, '∩');
  parsed = parsed.replace(/\\cup/g, '∪');
  parsed = parsed.replace(/\\land/g, '∧');
  parsed = parsed.replace(/\\lor/g, '∨');
  parsed = parsed.replace(/\\neg/g, '¬');
  parsed = parsed.replace(/\\exists/g, '∃');
  parsed = parsed.replace(/\\forall/g, '∀');
  
  // Common symbols
  parsed = parsed.replace(/\\%/g, '%');
  parsed = parsed.replace(/\\&/g, '&');
  parsed = parsed.replace(/\\#/g, '#');
  parsed = parsed.replace(/\\\\/g, '<br>');
  parsed = parsed.replace(/~/g, ' '); // non-breaking space in TeX
  
  // Greek letters (lowercase)
  parsed = parsed.replace(/\\alpha/g, 'α');
  parsed = parsed.replace(/\\beta/g, 'β');
  parsed = parsed.replace(/\\gamma/g, 'γ');
  parsed = parsed.replace(/\\delta/g, 'δ');
  parsed = parsed.replace(/\\epsilon/g, 'ε');
  parsed = parsed.replace(/\\varepsilon/g, 'ε');
  parsed = parsed.replace(/\\zeta/g, 'ζ');
  parsed = parsed.replace(/\\eta/g, 'η');
  parsed = parsed.replace(/\\theta/g, 'θ');
  parsed = parsed.replace(/\\vartheta/g, 'ϑ');
  parsed = parsed.replace(/\\iota/g, 'ι');
  parsed = parsed.replace(/\\kappa/g, 'κ');
  parsed = parsed.replace(/\\lambda/g, 'λ');
  parsed = parsed.replace(/\\mu/g, 'μ');
  parsed = parsed.replace(/\\nu/g, 'ν');
  parsed = parsed.replace(/\\xi/g, 'ξ');
  parsed = parsed.replace(/\\pi/g, 'π');
  parsed = parsed.replace(/\\varpi/g, 'ϖ');
  parsed = parsed.replace(/\\rho/g, 'ρ');
  parsed = parsed.replace(/\\varrho/g, 'ϱ');
  parsed = parsed.replace(/\\sigma/g, 'σ');
  parsed = parsed.replace(/\\varsigma/g, 'ς');
  parsed = parsed.replace(/\\tau/g, 'τ');
  parsed = parsed.replace(/\\upsilon/g, 'υ');
  parsed = parsed.replace(/\\phi/g, 'φ');
  parsed = parsed.replace(/\\varphi/g, 'ϕ');
  parsed = parsed.replace(/\\chi/g, 'χ');
  parsed = parsed.replace(/\\psi/g, 'ψ');
  parsed = parsed.replace(/\\omega/g, 'ω');
  
  // Greek letters (uppercase)
  parsed = parsed.replace(/\\Gamma/g, 'Γ');
  parsed = parsed.replace(/\\Delta/g, 'Δ');
  parsed = parsed.replace(/\\Theta/g, 'Θ');
  parsed = parsed.replace(/\\Lambda/g, 'Λ');
  parsed = parsed.replace(/\\Xi/g, 'Ξ');
  parsed = parsed.replace(/\\Pi/g, 'Π');
  parsed = parsed.replace(/\\Sigma/g, 'Σ');
  parsed = parsed.replace(/\\Upsilon/g, 'Υ');
  parsed = parsed.replace(/\\Phi/g, 'Φ');
  parsed = parsed.replace(/\\Psi/g, 'Ψ');
  parsed = parsed.replace(/\\Omega/g, 'Ω');
  
  // Links
  if (allowLinks) {
    parsed = parsed.replace(/\\href\{([^}]*)\}\{([^}]*)\}/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$2</a>');
    parsed = parsed.replace(/\\url\{([^}]*)\}/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline break-all">$1</a>');
  }
  
  // Clean up remaining simple braces (but preserve complex nested ones)
  parsed = parsed.replace(/\{([^{}]*)\}/g, '$1');

  // Anything still carrying LaTeX syntax (unknown macros, bare sub/superscripts)
  // is handed to KaTeX rather than printed with raw backslashes.
  parsed = promoteUnknownMacros(parsed, stash);

  return stash.restore(parsed);

};

/**
 * Public entry point: split the input into text and math segments, render math
 * with KaTeX and text with the (accent + symbol) text pipeline above.
 */
export const parseTexToHtml = (text: string, options: TexParseOptions = {}): string => {
  if (!text) return '';

  const { allowMath = true } = options;
  if (!allowMath) return parseTextSegment(text, options);

  return splitMathSegments(text)
    .map((segment) =>
      segment.type === 'math'
        ? renderMath(segment.value, segment.display)
        : parseTextSegment(segment.value, options),
    )
    .join('');
};


/**
 * Extract plain text from TeX (remove all commands)
 */
export const texToPlainText = (text: string): string => {
  if (!text) return '';

  // Drop math delimiters first so `\[ … \]` / `\( … \)` wrappers never leak
  // into meta descriptions or draft-metadata prefill.
  let plain = applyLatexAccents(
    text
      .replace(/\\\[|\\\]|\\\(|\\\)/g, ' ')
      .replace(/\$\$/g, ' '),
  );

  // Remove all TeX commands but keep their content
  plain = plain.replace(/\\[a-zA-Z]+\{([^}]*)\}/g, '$1');
  plain = plain.replace(/\\[a-zA-Z]+/g, '');
  plain = plain.replace(/\$([^$]+)\$/g, '$1');
  plain = plain.replace(/[{}]/g, '');
  plain = plain.replace(/~/g, ' ');
  plain = plain.replace(/[ \t]{2,}/g, ' ');

  return plain.trim();
};

